import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
//
import {
  Card,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '@/components';
import { NewPasswordFormValuesProps } from '@/features/auth/types';
import {
  NEW_PASSWORD_SCHEMA,
  NEW_PWD_FORM_DEFAULT_VALUES,
} from '@/features/auth/utils';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import SectionTitle from './SectionTitle';
import { resetUserPassword, setUserPassword } from '../api';

export default function UserPasswordReset({
  userId,
  currentEmail,
}: {
  userId: number | string | undefined;
  currentEmail: string | undefined;
}) {
  const { successMsg, errorMsg } = useSnackbarMsg();
  const [email, setEmail] = useState(currentEmail ?? '');
  const [isSendingResetLink, setIsSendingResetLink] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { control, handleSubmit, reset } = useForm<NewPasswordFormValuesProps>({
    resolver: yupResolver(NEW_PASSWORD_SCHEMA),
    defaultValues: NEW_PWD_FORM_DEFAULT_VALUES,
  });

  useEffect(() => {
    setEmail(currentEmail ?? '');
  }, [currentEmail]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleSubmitReset = async () => {
    if (!email || email === '') {
      errorMsg('Email is required');
      return;
    }

    setIsSendingResetLink(true);
    try {
      await resetUserPassword(email);
      successMsg('Password reset link sent');
    } catch (error) {
      errorMsg(error, 'There was an error while sending link');
    } finally {
      setIsSendingResetLink(false);
    }
  };

  const handleSetPassword = handleSubmit(
    async ({
      new_password,
      confirm_password,
    }: NewPasswordFormValuesProps) => {
      const parsedUserId = Number(userId);

      if (!Number.isFinite(parsedUserId)) {
        errorMsg('User id is required');
        return;
      }

      setIsSettingPassword(true);
      try {
        await setUserPassword(parsedUserId, new_password, confirm_password);
        successMsg('Password successfully updated.');
        reset(NEW_PWD_FORM_DEFAULT_VALUES);
      } catch (error) {
        errorMsg(error, "Couldn't update password");
      } finally {
        setIsSettingPassword(false);
      }
    }
  );

  const renderPasswordAdornment = () => (
    <InputAdornment position="end">
      <IconButton
        edge="end"
        onClick={() => setShowPassword((currentValue) => !currentValue)}
      >
        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
      </IconButton>
    </InputAdornment>
  );

  return (
    <Stack direction="column" spacing={2} component={Card} sx={{ p: 3 }}>
      <SectionTitle title="Password" />
      <TextField
        label="Email"
        value={email}
        onChange={handleEmailChange}
        size="small"
      />
      <LoadingButton
        variant="contained"
        onClick={handleSubmitReset}
        loading={isSendingResetLink}
      >
        Send Reset Link
      </LoadingButton>
      <Controller
        name="new_password"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            error={!!error}
            helperText={error?.message}
            inputProps={{ autoComplete: 'new-password' }}
            InputProps={{ endAdornment: renderPasswordAdornment() }}
          />
        )}
      />
      <Controller
        name="confirm_password"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            error={!!error}
            helperText={error?.message}
            inputProps={{ autoComplete: 'new-password' }}
            InputProps={{ endAdornment: renderPasswordAdornment() }}
          />
        )}
      />
      <LoadingButton
        variant="outlined"
        onClick={handleSetPassword}
        loading={isSettingPassword}
      >
        Set Password
      </LoadingButton>
    </Stack>
  );
}
